/**
 * Embeddable widget registry (FRIDGE-356) — the ONE list of `/embed/<asset>`
 * widgets, read by the route (app/embed/[asset]/page.tsx), the "Embed this"
 * cards on the source pages (components/data/embed-this-card.tsx), the framing
 * header rule in next.config.js (by path prefix), and the tests. Adding a
 * widget means adding a row here and a renderer in app/embed/_components.
 *
 * Every widget renders from the same read model its source page uses — never a
 * second copy of the numbers — and carries one visible attribution link back
 * to the canonical page. The embed pages are `noindex`: the canonical /data or
 * /tools page holds the search equity; the widget's job is to travel.
 *
 * Pure data + string builders (no React, no Next imports) so both server and
 * client modules can import it.
 */
import { SITE_ORIGIN } from '@/lib/urls'

export const EMBED_ASSET_SLUGS = [
  'price-index',
  'space-efficiency',
  'energy-index',
  'size-calculator',
  'energy-cost-calculator',
  'recalls',
] as const

export type EmbedAssetSlug = (typeof EMBED_ASSET_SLUGS)[number]

/**
 * The widgets `app/embed/[asset]/page.tsx` prerenders — the ones whose read
 * model is a committed JSON release or a pure function, with no request-time
 * data (FRIDGE-356).
 *
 * `recalls` is deliberately NOT one of them (FRIDGE-906). Its list comes from
 * the live recall record, so it ships as its own hourly-revalidated route at
 * `app/embed/recalls/page.tsx` — a static route segment, which Next resolves
 * ahead of the `[asset]` dynamic one. Registering it HERE anyway is the point:
 * this file stays the ONE list of widgets, so the "Embed this" cards, the
 * media kit's chart roster, the embed-code page and the tests all see it
 * without anyone having to remember a second place.
 */
export const PRERENDERED_EMBED_SLUGS = EMBED_ASSET_SLUGS.filter(
  (slug) => slug !== 'recalls'
) as ReadonlyArray<Exclude<EmbedAssetSlug, 'recalls'>>

export interface EmbedAsset {
  slug: EmbedAssetSlug
  /** Widget name as shown in the widget header and the iframe `title`. */
  name: string
  /** 'data' widgets attribute "Data: Fridge.com"; 'tool' widgets "Calculator: Fridge.com". */
  kind: 'data' | 'tool'
  /** Site-relative canonical page the attribution links to (the equity holder). */
  canonicalPath: string
  /** Eyebrow line in the widget header. */
  eyebrow: string
  /** One-line description shown on the source page's "Embed this" card. */
  description: string
  /**
   * Iframe height in the copy-ready snippet (px). MEASURED, not guessed: each
   * widget was rendered at 290, 320, 480 and 640px of frame width and its
   * <main> height read (08/17/2026, dev server) — the per-asset figures are in
   * the comment above each `height` below.
   *
   * The value is the TALLEST measurement (always the narrowest frame, where the
   * columns stack), rounded up. 290px is measured deliberately rather than
   * stopping at the 300px minimum the embed card states: if the content ever
   * overflows, the frame's own scrollbar steals ~15px of width, which makes the
   * content taller still, which keeps the scrollbar — so the height must clear
   * the layout at the post-scrollbar width, not at the nominal one.
   *
   * Wider frames simply leave the host page's background showing below the
   * card: the widget page background is transparent (app/embed/layout.tsx), so
   * spare height is invisible on a publisher's page while a short frame would
   * clip. Re-measure when a widget's content changes.
   */
  height: number
  /** Iframe max width in the copy-ready snippet (px). */
  maxWidth: number
}

export const EMBED_ASSETS: Record<EmbedAssetSlug, EmbedAsset> = {
  'price-index': {
    slug: 'price-index',
    name: 'Appliance Price Index',
    kind: 'data',
    canonicalPath: '/data/price-index',
    eyebrow: 'Fridge.com Data · Matched-model index',
    description:
      'The all-refrigerators index reading, its month, and the published trend as a sparkline — the same figures the index page tables show.',
    // Measured 378 / 357 / 337 / 286 at 290 / 320 / 480 / 640px.
    height: 390,
    maxWidth: 640,
  },
  'space-efficiency': {
    slug: 'space-efficiency',
    name: 'Space Efficiency Report',
    kind: 'data',
    canonicalPath: '/data/space-efficiency',
    eyebrow: 'Fridge.com Data · Storage per square foot',
    description:
      'The five appliance classes that convert floor space into storage best — median cubic feet per square foot, from the class table.',
    // Measured 560 / 527 / 454 / 454 at 290 / 320 / 480 / 640px.
    height: 570,
    maxWidth: 640,
  },
  'energy-index': {
    slug: 'energy-index',
    name: 'Refrigerator Energy-Efficiency Index',
    kind: 'data',
    canonicalPath: '/data/energy-index',
    eyebrow: 'Fridge.com Data · Energy-efficiency benchmark',
    description:
      'The median certified annual energy use across the certified set, and the most efficient published classes per cubic foot.',
    // Measured 679 / 642 / 607 / 540 at 290 / 320 / 480 / 640px.
    height: 690,
    maxWidth: 640,
  },
  'size-calculator': {
    slug: 'size-calculator',
    name: 'Refrigerator Size Calculator',
    kind: 'tool',
    canonicalPath: '/tools/refrigerator-size-calculator',
    eyebrow: 'Fridge.com Tools · Size & capacity',
    description:
      'Household size, cooking and shopping habits in — the recommended capacity range out. The same Fridge.com planning estimate as the full tool.',
    // Measured 585 / 585 / 342 / 337 at 290 / 320 / 480 / 640px (two columns from 420px).
    height: 600,
    maxWidth: 640,
  },
  'energy-cost-calculator': {
    slug: 'energy-cost-calculator',
    name: 'Energy Cost Calculator',
    kind: 'tool',
    canonicalPath: '/tools/energy-cost-calculator',
    eyebrow: 'Fridge.com Tools · Energy & cost',
    description:
      'Annual kWh from the EnergyGuide label and a local electricity rate in — the yearly, monthly and daily running cost out. Same arithmetic as the full tool.',
    // Measured 601 / 564 / 368 / 347 at 290 / 320 / 480 / 640px (two columns from 420px).
    height: 620,
    maxWidth: 640,
  },
  recalls: {
    slug: 'recalls',
    name: 'Refrigerator Recall Tracker',
    kind: 'data',
    canonicalPath: '/recalls',
    eyebrow: 'Fridge.com Data · CPSC recall record',
    description:
      'The five newest refrigerator and freezer recalls on record, each with its hazard and the models that carry a FIN — the same record the recall hub lists.',
    // Measured 476 / 460 / 396 / 380 at 290 / 320 / 480 / 640px (dev server,
    // 09/05/2026, five items). Tallest measurement rounded up, as above.
    height: 490,
    maxWidth: 640,
  },
}

export function isEmbedAssetSlug(value: string): value is EmbedAssetSlug {
  return (EMBED_ASSET_SLUGS as readonly string[]).includes(value)
}

/** Absolute URL of the widget page, e.g. https://fridge.com/embed/price-index. */
export function embedPageUrl(slug: EmbedAssetSlug): string {
  return `${SITE_ORIGIN}/embed/${slug}`
}

/** Absolute URL of the canonical source page the attribution links to. */
export function embedCanonicalUrl(slug: EmbedAssetSlug): string {
  return `${SITE_ORIGIN}${EMBED_ASSETS[slug].canonicalPath}`
}

/**
 * The widget page's <title>, in the binding sitewide 3-segment formula
 * (Target Keyword | Benefit | Fridge.com). The page is noindex; the title
 * still follows the formula because the formula binds every page.
 */
export function embedPageTitle(slug: EmbedAssetSlug): string {
  return `${EMBED_ASSETS[slug].name} Widget | Embed It on Your Site With Attribution | Fridge.com`
}

/**
 * The visible attribution line every widget carries — the attribution IS the
 * link, so it renders as a normal anchor (no nofollow) to the canonical page.
 * Data widgets: "Data: Fridge.com · CC BY 4.0"; calculators: "Calculator:
 * Fridge.com · CC BY 4.0" (a calculator has no dataset to call "data").
 */
export function embedAttributionLabel(slug: EmbedAssetSlug): string {
  return `${EMBED_ASSETS[slug].kind === 'data' ? 'Data' : 'Calculator'}: Fridge.com · CC BY 4.0`
}

/**
 * Copy-ready embed snippet: the iframe plus a visible caption line whose link
 * sits in the HOST page's own HTML. The link inside the iframe belongs to the
 * fridge.com document, so the caption is what puts a real backlink on the
 * embedding page — the same mechanism that made the /widgets script embeds
 * the site's link magnet. Absolute fridge.com URLs throughout (publishers
 * paste this into their own pages).
 */
export function embedIframeSnippet(slug: EmbedAssetSlug): string {
  const asset = EMBED_ASSETS[slug]
  return [
    `<iframe src="${embedPageUrl(slug)}" title="${asset.name} — Fridge.com" width="100%" height="${asset.height}" style="border:0;max-width:${asset.maxWidth}px;display:block" loading="lazy"></iframe>`,
    `<p style="margin:6px 0 0;font:13px/1.4 system-ui,sans-serif;color:#4b5563">${asset.name} — <a href="${embedCanonicalUrl(slug)}" style="color:#0469f0;font-weight:600">${embedAttributionLabel(slug)}</a></p>`,
  ].join('\n')
}

/**
 * Script-free fallback: a static line + link, for surfaces that strip iframes
 * (newsletters, AMP, locked-down CMS blocks). Evergreen — it names the asset
 * and links to the canonical page, and freezes no figure into someone else's
 * page.
 */
export function embedFallbackSnippet(slug: EmbedAssetSlug): string {
  const asset = EMBED_ASSETS[slug]
  return `<p><a href="${embedCanonicalUrl(slug)}">Fridge.com ${asset.name}</a> — ${embedAttributionLabel(slug)}</p>`
}
